<template>
    <div class="table-responsive">
        <loader v-if="isLoading" />
        <b-table-simple v-else borderless hover class="list">
            <colgroup>
                <col style="width: 8%;" />
                <col style="width: 22%;" />
                <col style="width: 8%;" />
                <col style="width: 8%;" />
                <col style="width: 8%;" />
                <col style="width: 32%;" />
                <col style="width: 10%;" />
                <col style="width: 4%;" />
            </colgroup>
            <b-thead>
                <b-tr>
                    <b-th>Credit:</b-th>
                    <b-th>Client:</b-th>
                    <b-th>Reason:</b-th>
                    <b-th>Amount:</b-th>
                    <b-th>Balance:</b-th>
                    <b-th>Notes:</b-th>
                    <b-th>Date:</b-th>
                    <b-th></b-th>
                </b-tr>
            </b-thead>
            <b-tbody>
                <b-tr v-for="credit in credits" :key="credit.id">
                    <b-td class="text-nowrap">
                        <strong
                            v-if="
                                _hasAccess('billing.credit.update') &&
                                credit.balance > 0 &&
                                credit.is_active_actor &&
                                !credit.isEditable
                            "
                            class="text-primary cursor-pointer"
                            @click="showEditModal(credit)"
                        >
                            {{ credit.name }}
                        </strong>
                        <span v-else>{{ credit.name }}</span>
                    </b-td>
                    <b-td>
                        <span v-if="credit.client">
                            <client-name
                                :client="credit.client"
                                :text="credit.client.display_name"
                            />
                        </span>
                    </b-td>
                    <b-td>
                        {{ credit.reason }}
                    </b-td>
                    <b-td>{{ _toCurrency(credit.amount) }}</b-td>
                    <b-td>{{ _toCurrency(credit.balance) }}</b-td>
                    <b-td v-html="convertNewLineToBreak(credit.note)"></b-td>
                    <b-td class="text-nowrap"> {{ _carbonDateDisplay(credit.credit_date) }}</b-td>
                    <b-td class="text-right" v-if="credit.payment && credit.payment.status == 0">
                        <span
                            class="badge bg-secondary"
                            style="font-weight: bold;"
                            v-b-tooltip.hover.top
                            title="The customer's bank is processing this payment"
                            >Pending <i class="fas fa-clock"></i
                        ></span>
                    </b-td>
                    <b-td v-else class="text-right">
                        <div v-if="checkAccess(credit)">
                            <b-dropdown
                                v-if="!credit.isEditable"
                                variant="link"
                                toggle-class="text-decoration-none"
                                no-caret
                                right
                            >
                                <template v-slot:button-content>
                                    <i class="cus-cog-down"></i>
                                </template>

                                <b-dropdown-item
                                    href="#"
                                    v-if="_hasAccess('billing.credit.update')"
                                    @click="showEditModal(credit)"
                                >
                                    <i class="cus-page-white-edit"></i>
                                    Edit Credit
                                </b-dropdown-item>
                                <b-dropdown-item
                                    href="#"
                                    v-if="getPermissionDelete(credit)"
                                    @click="confirmDelete(credit)"
                                >
                                    <i class="cus-delete"></i>
                                    Delete Credit
                                </b-dropdown-item>
                                <b-dropdown-item
                                    href="#"
                                    v-if="
                                        _hasAccess('billing.credit.update') &&
                                        _hasAccess('billing.refund.create') &&
                                        credit.cc_processor
                                    "
                                    @click="showRefundCardModal(credit)"
                                >
                                    <i class="cus-arrow-refresh"></i>
                                    Refund to Card
                                </b-dropdown-item>
                                <b-dropdown-item
                                    href="#"
                                    v-if="
                                        _hasAccess('billing.credit.update') &&
                                        _hasAccess('billing.refund.create')
                                    "
                                    @click="showConvertModal(credit)"
                                >
                                    <i class="cus-arrow-refresh"></i>
                                    Convert to Refund
                                </b-dropdown-item>
                                <b-dropdown-item
                                    href="#"
                                    v-if="
                                        _hasAccess('billing.credit.update') &&
                                        credit.balance > 0
                                    "
                                    @click="showConvertToFundCardModal(credit)"
                                >
                                    <img src='assets/icons/donation.png' class='pr-1' style="width: 22px"/>
                                    Donate to Charitable Fund
                                </b-dropdown-item>
                            </b-dropdown>
                            <b-dropdown
                                variant="link"
                                toggle-class="text-decoration-none"
                                v-else-if="_hasAccess('billing.refund.create')"
                                no-caret
                                right
                            >
                                <template v-slot:button-content>
                                    <i class="cus-cog-down"></i>
                                </template>
                                <b-dropdown-item
                                    href="#"
                                    v-if="credit.cc_processor"
                                    @click="showRefundCardModal(credit)"
                                >
                                    <i class="cus-arrow-refresh"></i>
                                    Refund to Card
                                </b-dropdown-item>
                                <b-dropdown-item
                                    href="#"
                                    @click="showConvertModal(credit)"
                                >
                                    <i class="cus-arrow-refresh"></i>
                                    Convert to Refund
                                </b-dropdown-item>
                                <b-dropdown-item
                                    href="#"
                                    v-if="
                                        _hasAccess('billing.credit.update') &&
                                        credit.balance > 0
                                    "
                                    @click="showConvertToFundCardModal(credit)"
                                >
                                    <img src='assets/icons/donation.png' class='pr-1' style="width: 22px;"/> 
                                    Donate to Charitable Fund
                                </b-dropdown-item>
                            </b-dropdown>
                        </div>
                    </b-td>
                </b-tr>
                <b-tr v-if="credits.length == 0 && !isLoading">
                    <b-td colspan="8"><i>No records available</i></b-td>
                </b-tr>
            </b-tbody>
        </b-table-simple>

        <b-pagination
            v-if="meta && showPagination"
            v-show="meta.lastPage > 1"
            align="right"
            v-model="meta.currentPage"
            :total-rows="meta.total"
            :per-page="meta.perPage"
            class="vet-pagination"
            prev-text="Prev"
            next-text="Next"
            :hide-goto-end-buttons="true"
            @change="getCredits"
        />
        <modal-edit-credit
            modal-id="modal-edit-credit"
            ref="modal-edit-credit"
            :client-name="selectedClient"
            @reload="reloadRelated()"
        />
        <modal-delete-credit
            modal-id="modal-delete-credit"
            ref="modal-delete-credit"
            :credit="selectedCredit"
            :is-client-page="true"
            @reload="reloadRelated()"
        />

        <modal-convert-to-refund
            modal-id="modal-convert-credit"
            ref="modal-convert-to-refund"
            :credit="selectedCredit"
            @reload="reloadRelated()"
        />

        <modal-refund-to-card
            modal-id="modal-refund-card"
            ref="modal-refund-to-card"
            :credit="selectedCredit"
            @reload="reloadRelated()"
        />

        <modal-convert-to-charitable-fund
            modal-id="modal-convert-to-charitable-fund"
            ref="modal-convert-to-charitable-fund"
            :credit="selectedCredit"
            @reload="reloadRelated()"
        />
    </div>
</template>

<script>
/**
 * @name CreditList
 * @description List of credits based on invoice
 * @see apps/application/views/staff/billing/sub/credit/list.php
 * @see apps/application/models/orm/TblCredit.php
 * @see apps/application/views/staff/billing/sub/credit/create_update.php
 * @see apps/assets/js/module/common/model/credit_model.js
 * @see apps/application/controllers/billing/credit.php
 * @see apps/application/views/staff/billing/sub/credit/refund_to_card.php
 * @see apps/application/views/staff/billing/sub/credit/convert.php
 */
import Loader from "@/components/elements/loader-list";
import BillingCreditServices from "@/services/billing/billing-credit";
import CBILLINGS from "@/constants/billings";
import ClientName from "@/components/commons/client-name";
import ModalDeleteCredit from "@/components/billing-credit/credit-delete-modal";
import ModalEditCredit from "@/components/billing-credit/credit-edit-modal";
import ModalConvertToRefund from "@/components/billing-credit/credit-convert-modal";
import ModalRefundToCard from "@/components/billing-credit/refund-card-modal";
import ModalConvertToCharitableFund from "@/components/billing-credit/credit-convert-to-charitable-fund-modal";

export default {
    mixins: {},
    components: {
        Loader,
        ClientName,
        ModalEditCredit,
        ModalDeleteCredit,
        ModalConvertToRefund,
        ModalRefundToCard,
        ModalConvertToCharitableFund
    },
    data() {
        return {
            isLoading: false,
            showPagination: false,
            credits: [],
            meta: null,
            selectedClient: null,
            selectedCredit: { name: null }
        };
    },
    mounted() {
        this.getCredits();
    },
    methods: {
        getCredits(page = 1) {
            let self = this;
            this.isLoading = true;

            BillingCreditServices.getCredits(this.PER_PAGE, page)
                .then(res => {
                    this.isLoading = false;
                    self.meta = self.standardizePagingKey(res);

                    if (self.meta.total > 20) {
                        self.showPagination = true;
                    }

                    self.credits = res.data.map(data => self.prepareData(data));
                })
                .catch(error => {
                    this._message(
                        "Error : Unable to get credit list",
                        "danger"
                    );
                    this.isLoading = false;
                    console.error(error);
                });
        },
        prepareData(data) {
            data.client = {
                display_name: data.client_name,
                name: data.client_name,
                encoded_id: data.client_encoded_id,
                id: data.client_id
            };
            data.payment_id = data.payment_type_id;
            data.type = data.credit_type_id;
            return data;
        },
        reloadRelated() {
            this.getCredits();
        },
        standardizePagingKey(res) {
            res.lastPage = res.last_page;
            res.currentPage = res.current_page;
            res.perPage = res.per_page;
            return res;
        },
        showEditModal(credit) {
            this.selectedCredit = credit;
            this.selectedClient = credit.client_name;
            let date = credit.credit_date
                ? this._fromDateCarbon(credit.credit_date).toFormat("X")
                : null;

            this.$store.dispatch("checkBillingLock", {
                date,
                onProceed: () => this.$refs["modal-edit-credit"].open(credit),
                onError: message => this._message(message, "danger")
            });
        },
        showConvertModal(credit) {
            let isUsed = false;
            BillingCreditServices.getUsedStatus(credit.id)
                .then(response => {
                    if (response && response.code == 200) {
                        if (response.isUsed == true) {
                            if (!response.withBalance) {
                                this._message(
                                    "This credit has already been applied and cannot be converted into a refund.",
                                    "danger"
                                );
                                isUsed = true;
                                this.reloadRelated();
                            }
                        }
                    }
                })
                .finally(() => {
                    if (!isUsed) {
                        this.selectedCredit = credit;
                        let date = credit.credit_date
                            ? this._fromDateCarbon(credit.credit_date).toFormat("X")
                            : null;

                        this.$store.dispatch("checkBillingLock", {
                            date,
                            onProceed: () => this.$bvModal.show("modal-convert-credit"),
                            onError: message => this._message(message, "danger")
                        });
                    }
                });
        },
        showRefundCardModal(credit) {
            let isUsed = false;
            BillingCreditServices.getUsedStatus(credit.id)
                .then(response => {
                    if (response && response.code == 200) {
                        if (response.isUsed == true) {
                            if (!response.withBalance) {
                                this._message(
                                    "This credit has already been applied and cannot be refunded.",
                                    "danger"
                                );
                                isUsed = true;
                                this.reloadRelated();
                            }
                        }
                    }
                })
                .finally(() => {
                    if (!isUsed) {
                        this.selectedCredit = credit;
                        let date = credit.credit_date
                            ? this._fromDateCarbon(credit.credit_date).toFormat("X")
                            : null;

                        this.$store.dispatch("checkBillingLock", {
                            date,
                            onProceed: () => this.$bvModal.show("modal-refund-card"),
                            onError: message => this._message(message, "danger")
                        });
                    }
                });
        },
        showConvertToFundCardModal(credit) {
            let isUsed = false;
            BillingCreditServices.getUsedStatus(credit.id)
                .then(response => {
                    if (response && response.code == 200) {
                        if (response.isUsed == true) {
                            if (!response.withBalance) {
                                this._message(
                                    "This credit has already been applied and cannot be donated to the charitable fund.",
                                    "danger"
                                );
                                isUsed = true;
                                this.reloadRelated();
                            }
                        }
                    }
                })
                .finally(() => {
                    if (!isUsed) {
                        this.selectedCredit = credit;
                        let date = credit.credit_date
                            ? this._fromDateCarbon(credit.credit_date).toFormat("X")
                            : null;

                        this.$store.dispatch("checkBillingLock", {
                            date,
                            onProceed: () => this.$bvModal.show("modal-convert-to-charitable-fund"),
                            onError: message => this._message(message, "danger")
                        });
                    }
                });
        },
        confirmDelete(credit) {
            let isUsed = false;
            BillingCreditServices.getUsedStatus(credit.id)
                .then(response => {
                    if (response && response.code == 200) {
                        if (response.isUsed == true) {
                            this._message(
                                "This credit has already been applied and cannot be deleted",
                                "danger"
                            );
                            isUsed = true;
                            this.reloadRelated();
                        }
                    }
                })
                .finally(() => {
                    if (!isUsed) {
                        this.selectedCredit = credit;
                        let date = credit.credit_date
                            ? this._fromDateCarbon(credit.credit_date).toFormat("X")
                            : null;

                        this.$store.dispatch("checkBillingLock", {
                            date,
                            onProceed: () => this.$bvModal.show("modal-delete-credit"),
                            onError: message => this._message(message, "danger")
                        });
                    }
                });
        },
        checkAccess(credit) {
            let haveAccess =
                credit.is_active_actor &&
                (this._hasAccess("billing.credit.update") ||
                    this._hasAccess("billing.credit.delete"));
            return haveAccess && credit.balance > 0;
        },
        getPermissionDelete(credit) {
            return (
                this._hasAccess("billing.credit.delete") &&
                credit.credit_type_id != CBILLINGS.CREDIT_PEER.TYPE_RETURN &&
                credit.credit_type_id !=
                    CBILLINGS.CREDIT_PEER.TYPE_DEPOSIT_OVERPAID &&
                credit.credit_type_id !=
                    CBILLINGS.CREDIT_PEER.TYPE_PAYMENT_OVERPAID &&
                credit.isDeletable
            );
        }
    }
};
</script>
